import { IconTextFormatWidget } from '@components/icons/text-format-widget';
import { TextEditor } from '@components/text-editor';
import { getFieldContent } from '@components/text-editors/addons/field-mapping';
import { getScopedTranslate } from '@locale/translate';
import { getRawTextFromHtml } from '@utils/browser';

import { FieldDropzone } from './field-dropzone';
import { useTextContentActions } from './use-field-actions';
import { FieldMappingDisplay, useFieldMappingEditor } from './use-field-mapping';

import { conditionsTab } from '../conditions-tab';
import { useFormBuilderContext } from '../context';
import { FieldEdit } from '../field-edit';
import { FieldConfig } from '../field-registry';
import { FieldSidebarLabel } from '../fields-sidebar';

type Config = FieldConfig<'html'>;

const Display: Config['Display'] = ({ field }) => {
  return <FieldMappingDisplay content={field.content} useRichText />;
};
const FormBuilderHtml: Config['Component'] = ({ field, dragHandle }) => {
  const { store } = useFormBuilderContext();
  const actions = store.actions;
  const menuItems = useTextContentActions(field);

  const { shouldUseFieldMapping, FieldMappingEditor } = useFieldMappingEditor({
    field,
    contentKey: 'content',
    sidebarLabel: t('title'),
    fieldType: 'paragraph',
  });

  const content = getFieldContent(field.content);

  const editContent = shouldUseFieldMapping ? (
    FieldMappingEditor
  ) : (
    <TextEditor
      variant="white"
      initialEditorState={content}
      onChangeHtml={(html) => actions.createChangeHandler(field, 'content')({ value: html })}
    >
      <TextEditor.Toolbar
        disableHeading
        anchorButtonProps={{
          save: t('core.save'),
          addLink: t('addLink'),
          linkToolTip: t('linkToolTip'),
          label: t('label'),
        }}
      />
      <TextEditor.Input stripPastedStyles />
    </TextEditor>
  );
  return (
    <FieldDropzone field={field}>
      <FieldEdit
        field={field}
        dragHandle={dragHandle}
        menu={menuItems}
        display={<Display field={field} />}
        edit={editContent}
      />
    </FieldDropzone>
  );
};

const { t } = getScopedTranslate({
  en: {
    title: 'Text',
    addLink: 'Add link',
    linkToolTip: 'Select what you want to turn into a hyperlink',
    label: 'Link',
  },
});

export const htmlConfig: Config = {
  kind: 'html',
  title: t('title'),
  group: 'content',
  Display,
  TreeDisplay: ({ field }) => (
    <FieldSidebarLabel label={getRawTextFromHtml(getFieldContent(field.content))} />
  ),
  Icon: IconTextFormatWidget,
  Component: FormBuilderHtml,
  propertiesTabs: [conditionsTab],
};
